import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faSpinner, 
  faTimes, 
  faCheckCircle, 
  faExclamationTriangle,
  faDownload,
  faFileExcel,
  faSeedling
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import * as ExcelJS from 'exceljs';
import api from '../../services/api';

interface DropdownOption {
  value: string;
  label_DE: string;
  label_EN?: string;
  sortOrder?: number;
  isActive?: boolean;
}

interface DropdownImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: string;
  categoryLabel: string;
  onImportComplete: (options: DropdownOption[]) => void;
}

const DropdownImportModal: React.FC<DropdownImportModalProps> = ({ 
  isOpen, 
  onClose, 
  category,
  categoryLabel,
  onImportComplete 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<DropdownOption[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(null);
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        
        // Convert worksheet to JSON data
        const jsonData: any[] = [];
        const headers: string[] = [];
        
        worksheet.eachRow((row, rowNumber) => {
          const rowData: any = {};
          row.eachCell((cell, colNumber) => {
            if (rowNumber === 1) {
              // Header row
              headers[colNumber - 1] = cell.value?.toString() || `Column${colNumber}`;
            } else {
              // Data rows
              const header = headers[colNumber - 1];
              if (header) {
                rowData[header] = cell.value;
              }
            }
          });
          
          if (rowNumber > 1 && Object.keys(rowData).length > 0) {
            jsonData.push(rowData);
          }
        });

        console.log('Raw Excel data:', jsonData);
        console.log('First row structure:', jsonData[0]);
        console.log('Available columns:', Object.keys(jsonData[0] || {}));

        const options: DropdownOption[] = jsonData.map((row: any, index: number) => {
          console.log(`Processing row ${index + 1}:`, row);
          
          // Für die neuen Kategorien (baseUnit, baseComparisonUnitCode, fangzone, fangmethode) 
          // verwenden wir _Value_DE sowohl für value als auch für label_DE wenn _Value_IND fehlt
          let value = '';
          let label_DE = '';
          
          if (row._Value_IND || row._Value || row.value || row.Value || row.CODE) {
            // Normale Mapping-Logik wenn separate Werte vorhanden sind
            value = row._Value_IND || row._Value || row.value || row.Value || row.CODE || '';
            label_DE = row._Value_DE || row.WEDLHerkunfslandBezeichnung || row.label_DE || row.Label || row.LABEL_DE || row.Bezeichnung || '';
          } else if (row._Value_DE) {
            // Für einfache Listen: _Value_DE wird sowohl für value als auch für label_DE verwendet
            value = row._Value_DE;
            label_DE = row._Value_DE;
          }
          
          const mappedOption = {
            value: value,
            label_DE: label_DE,
            label_EN: row.MarkantContryOfOriginISOCodeALPHA3 || row.label_EN || row.Label_EN || row.LABEL_EN || undefined,
            sortOrder: row.sortOrder || row.Sort || (index + 1),
            isActive: row.isActive !== undefined ? row.isActive : true
          };
          
          console.log(`Row ${index + 1} mapping:`, {
            originalRow: row,
            mappedOption: mappedOption,
            hasValue: !!mappedOption.value,
            hasLabel: !!mappedOption.label_DE
          });
          
          return mappedOption;
        }).filter(option => {
          const isValid = option.value && option.label_DE;
          if (!isValid) {
            console.log('Filtered out option (missing value or label_DE):', option);
          }
          return isValid;
        });

        setPreviewData(options);
        setShowPreview(true);
        console.log('Parsed options:', options);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setError('Fehler beim Lesen der Excel-Datei');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!previewData.length) {
      setError('Keine Daten zum Importieren');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      console.log(`[DropdownImport] Starting import for category: ${category}`);
      console.log(`[DropdownImport] Preview data length: ${previewData.length}`);
      console.log(`[DropdownImport] Replace existing: ${replaceExisting}`);
      console.log(`[DropdownImport] Sample data:`, previewData.slice(0, 3));
      
      // Use different API endpoint for markantUnitDescriptor
      let apiUrl: string;
      if (category === 'markantUnitDescriptor') {
        apiUrl = '/public/portal/markant-unit-descriptors/import';
      } else {
        apiUrl = `/dropdown-options/${category}/import`;
      }
      
      const response = await api.post(apiUrl, {
        options: previewData,
        replaceExisting
      });

      console.log('Import response:', response.data);
      
      setSuccess(`${response.data.summary.imported} ${categoryLabel}-Optionen erfolgreich importiert`);
      onImportComplete(response.data.data);
      
      // Reset nach kurzer Zeit
      setTimeout(() => {
        onClose();
        resetModal();
      }, 2000);

    } catch (error: any) {
      console.error('Import error:', error);
      setError(error.response?.data?.message || 'Fehler beim Importieren');
    } finally {
      setImporting(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);

    try {
      const response = await api.post(`/dropdown-options/${category}/seed`);
      
      setSuccess(`${response.data.data.length} Standard-${categoryLabel}-Optionen erstellt`);
      onImportComplete(response.data.data);
      
      setTimeout(() => {
        onClose();
        resetModal();
      }, 2000);

    } catch (error: any) {
      console.error('Seed error:', error);
      setError(error.response?.data?.message || 'Fehler beim Erstellen der Standard-Optionen');
    } finally {
      setSeeding(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
    setPreviewData([]);
    setShowPreview(false);
    setReplaceExisting(false);
  };

  const downloadTemplate = async () => {
    const templateData = [
      { _Value_DE: 'Beispiel Option 1', _Value_IND: 'BEISPIEL1', sortOrder: 1, isActive: true },
      { _Value_DE: 'Beispiel Option 2', _Value_IND: 'BEISPIEL2', sortOrder: 2, isActive: true },
      { _Value_DE: 'Kilogramm', _Value_IND: 'KG', sortOrder: 3, isActive: true },
      { _Value_DE: 'Stück', _Value_IND: 'STK', sortOrder: 4, isActive: true }
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('DropdownOptions');
    
    // Add headers
    const headers = ['_Value_DE', '_Value_IND', 'sortOrder', 'isActive'];
    worksheet.addRow(headers);
    
    // Add data rows
    templateData.forEach(row => {
      worksheet.addRow([row._Value_DE, row._Value_IND, row.sortOrder, row.isActive]);
    });
    
    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${categoryLabel}_Template.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{categoryLabel} Import</h3>
            <p className="text-sm text-gray-500">Excel-Datei importieren oder Standard-Optionen erstellen</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={importing || seeding}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-6">
          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          )}

          {/* Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Excel Import */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-green-600" />
                Excel Import
              </h4>
              
              <div className="mb-4">
                <button
                  onClick={downloadTemplate}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                  Excel-Vorlage herunterladen
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  disabled={importing || seeding}
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                    className="mr-2"
                    disabled={importing || seeding}
                  />
                  <span className="text-sm text-gray-700">Bestehende Optionen ersetzen</span>
                </label>
              </div>
            </div>

            {/* Seed Standard Options */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <FontAwesomeIcon icon={faSeedling} className="mr-2 text-green-600" />
                Standard-Optionen
              </h4>
              
              <p className="text-sm text-gray-600 mb-4">
                Erstellt automatisch Standard-{categoryLabel}-Optionen
              </p>

              <button
                onClick={handleSeed}
                disabled={seeding || importing}
                className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
              >
                {seeding ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faSeedling} className="mr-2" />
                )}
                {seeding ? 'Erstelle...' : 'Standard-Optionen erstellen'}
              </button>
            </div>
          </div>

          {/* Preview */}
          {showPreview && previewData.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Vorschau ({previewData.length} Optionen)</h4>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Wert</th>
                      <th className="px-3 py-2 text-left">Bezeichnung (DE)</th>
                      <th className="px-3 py-2 text-left">Bezeichnung (EN)</th>
                      <th className="px-3 py-2 text-center">Reihenfolge</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {previewData.slice(0, 5).map((option, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">{option.value}</td>
                        <td className="px-3 py-2">{option.label_DE}</td>
                        <td className="px-3 py-2 text-gray-500">{option.label_EN || '-'}</td>
                        <td className="px-3 py-2 text-center">{option.sortOrder}</td>
                      </tr>
                    ))}
                    {previewData.length > 5 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-center text-gray-500">
                          ... und {previewData.length - 5} weitere Optionen
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Button */}
          {showPreview && previewData.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={handleImport}
                disabled={importing || seeding}
                className="bg-blue-600 text-white py-2 px-6 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {importing ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faUpload} className="mr-2" />
                )}
                {importing ? 'Importiere...' : `${previewData.length} Optionen importieren`}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DropdownImportModal;
